import React, { useState, useEffect } from 'react';
import WorkoutTile from '../../../../component/HealthComponent/workoutTile';
import { useNavigate } from 'react-router-dom';
import { DeleteApi } from '../../../../api/api';
import { errorToast, successToast, getLocalStorageItem } from '../../../../utils/helper';
import { getWorkoutPlans } from '../../../../api/healthAPI';

const FitnessWorkoutPlans = ({ searchKey }) => {
  const navigate = useNavigate();
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    fetchWorkoutPlans();
  }, [searchKey]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const fetchWorkoutPlans = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 6, // Show only 6 workout plans on dashboard
        status: 'published', // Only fetch published workout plans for dashboard
        ...(searchKey && { search: searchKey })
      };
      const response = await getWorkoutPlans(params);
      if (response?.data) {
        setWorkoutPlans(response.data);
      } else {
        setWorkoutPlans([]);
      }
    } catch (error) {
      console.error('Error fetching workout plans:', error);
      setWorkoutPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkoutPlanClick = (workoutPlan) => {
    navigate(
      `${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans/${workoutPlan._id}`,
      {
        state: { _id: workoutPlan._id, action: 'view' }
      }
    );
  };

  const handleMenuClick = (e, workoutPlan) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === workoutPlan._id ? null : workoutPlan._id);
  };

  const handleView = (workoutPlan) => {
    navigate(
      `${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans/${workoutPlan._id}`,
      {
        state: { _id: workoutPlan._id, action: 'view' }
      }
    );
    setOpenMenuId(null);
  };

  const handleEdit = (workoutPlan) => {
    navigate(
      `${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans/new?id=${workoutPlan._id}`,
      {
        state: { _id: workoutPlan._id, action: 'edit' }
      }
    );
    setOpenMenuId(null);
  };

  const handleDeletePlan = async (workoutPlan) => {
    if (window.confirm(`Are you sure you want to delete "${workoutPlan.title}"?`)) {
      try {
        // TODO: Implement delete API call
        // const response = await DeleteApi(`admin/v1/workout-plan/${workoutPlan.id}`);
        // if (response?.data?.meta?.code === 1) {
        //   successToast('Workout plan deleted successfully');
        //   // Refresh the list or update state
        //   // fetchWorkoutPlans();
        // } else {
        //   throw new Error(response?.data?.meta?.message || 'Failed to delete workout plan');
        // }
        console.log('Delete workout plan:', workoutPlan.id);
        successToast('Delete functionality will be implemented soon');
      } catch (error) {
        console.error('Error deleting workout plan:', error);
        errorToast(error.message || 'Failed to delete workout plan');
      }
    }
  };

  const handleViewAll = () => {
    navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans`);
  };

  return (
    <div className="bg-white mt-4 rounded-lg p-6">
      {/* Header with View All button */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Workout Plans</h2>
        <button
          onClick={handleViewAll}
          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm flex items-center gap-1 transition-colors"
        >
          View All
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Workout Plans Grid */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}
      {!loading && workoutPlans.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {workoutPlans.map((workoutPlan) => (
            <div key={workoutPlan._id} className="relative">
              <WorkoutTile
                image={workoutPlan.thumbnail_url}
                title={workoutPlan.plan_name}
                usageCount={workoutPlan.usage_count || 0}
                badgeText={workoutPlan.workout_focus || workoutPlan.difficulty || 'Workout Plan'}
                onClick={() => handleWorkoutPlanClick(workoutPlan)}
                onMenuClick={(e) => handleMenuClick(e, workoutPlan)}
              />

              {/* Dropdown Menu */}
              {openMenuId === workoutPlan._id && (
                <div className="absolute top-12 right-2 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]">
                  <button
                    onClick={() => handleView(workoutPlan)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    View
                  </button>
                  <button
                    onClick={() => handleEdit(workoutPlan)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeletePlan(workoutPlan)}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State (optional - show when no workout plans) */}
      {!loading && workoutPlans.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No workout plans</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new workout plan.</p>
        </div>
      )}
    </div>
  );
};

export default FitnessWorkoutPlans;
